import Knock, { Feed, FeedEventPayload } from '@knocklabs/client';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { subscribe, eventBus } from 'mobx-event-bus2';

import RootStore from 'stores/Root/Root';
import API from 'utils/api';
import { NotificationType } from 'utils/api/modules/user/user.types';
import Logger from 'utils/logger';
import { EventType } from 'utils/events/constants';

import { NotificationCategory, getCategoryForNotification } from 'containers/KnockNotifications/constants';

import { knockItemToNotification } from './knockAdapter';

const KNOCK_ENABLED_CACHE_KEY = 'knockEnabled';

function readCachedKnockEnabled(): boolean | undefined {
  try {
    const raw = localStorage.getItem(KNOCK_ENABLED_CACHE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return undefined;
  } catch {
    return undefined;
  }
}

function writeCachedKnockEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(KNOCK_ENABLED_CACHE_KEY, String(enabled));
  } catch {
    // localStorage can throw in private mode / SSR — fail silently
  }
}

function clearCachedKnockEnabled(): void {
  try {
    localStorage.removeItem(KNOCK_ENABLED_CACHE_KEY);
  } catch {
    // ignore
  }
}

export default class KnockInbox {
  store: RootStore;

  api: typeof API;

  @observable notifications: NotificationType[] | undefined;

  @observable unreadCount = 0;

  @observable isLoading = false;

  @observable initialSync?: boolean = undefined;

  @observable hasMore = false;

  @observable isDismissing = false;

  @observable activeCategory: NotificationCategory = NotificationCategory.ALL;

  @observable showUnreadOnly = false;

  @observable searchQuery = '';

  // Seeded from localStorage so the sidebar's first paint matches the previous session.
  @observable knockEnabled?: boolean = readCachedKnockEnabled();

  private knockClient: Knock | null = null;

  private knockFeed: Feed | null = null;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.store = rootStore;
    this.api = api;

    eventBus.register(this);
  }

  @action.bound
  async loadInitialData(): Promise<void> {
    if (this.initialSync !== undefined) {
      return;
    }

    this.initialSync = false;
    this.isLoading = true;

    try {
      const { data } = await this.api.user.getKnockToken()();

      if (!data.enabled || !data.token || !data.publicApiKey || !data.feedChannelId || !data.userId) {
        writeCachedKnockEnabled(false);
        runInAction(() => {
          this.knockEnabled = false;
          this.initialSync = true;
          this.isLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.knockEnabled = true;
      });

      const client = new Knock(data.publicApiKey);
      client.authenticate(data.userId, data.token);

      const feed = client.feeds.initialize(data.feedChannelId);

      feed.on('items.received.page', (payload: FeedEventPayload) => {
        runInAction(() => {
          const newItems = payload.items.map(knockItemToNotification);
          if (this.notifications === undefined) {
            this.notifications = [];
          }
          const existingIds = new Set(this.notifications.map((n) => n.uuid));
          this.notifications.push(...newItems.filter((n) => !existingIds.has(n.uuid)));
          this.unreadCount = payload.metadata.unread_count;
          this.hasMore = this.notifications.length < payload.metadata.total_count;
        });
      });

      feed.on('items.received.realtime', (payload: FeedEventPayload) => {
        runInAction(() => {
          const newItems = payload.items.map(knockItemToNotification);
          if (this.notifications === undefined) {
            this.notifications = [];
          }
          const existingIds = new Set(this.notifications.map((n) => n.uuid));
          const fresh = newItems.filter((n) => !existingIds.has(n.uuid));
          this.notifications.unshift(...fresh);
          this.unreadCount = payload.metadata.unread_count;
        });
      });

      feed.listenForUpdates();
      await feed.fetch();

      writeCachedKnockEnabled(true);
      runInAction(() => {
        this.knockClient = client;
        this.knockFeed = feed;
        this.initialSync = true;
      });

      this.scheduleTokenRefresh();
    } catch (e) {
      Logger.error('Failed to initialize Knock inbox', e);
      runInAction(() => {
        this.initialSync = true;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async loadNotifications(): Promise<void> {
    if (!this.hasMore || !this.knockFeed || this.isLoading) {
      return;
    }

    this.isLoading = true;
    try {
      await this.knockFeed.fetchNextPage();
    } catch (e) {
      Logger.error('Failed to load more Knock notifications', e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async dismissNotifications(): Promise<void> {
    if (!this.knockFeed || !this.notifications) {
      return;
    }

    this.isDismissing = true;
    try {
      await this.knockFeed.markAllAsRead();
      runInAction(() => {
        this.notifications = this.notifications?.map((n) => ({ ...n, dismissed: true }));
        this.unreadCount = 0;
      });
    } catch (e) {
      Logger.error('Failed to dismiss Knock notifications', e);
    } finally {
      runInAction(() => {
        this.isDismissing = false;
      });
    }
  }

  @action.bound
  async markNotificationAsRead(notificationId: string): Promise<void> {
    if (!this.knockFeed) {
      return;
    }

    const feedItem = this.knockFeed.getState().items.find((item) => item.id === notificationId);
    if (!feedItem || feedItem.read_at) {
      return;
    }

    try {
      await this.knockFeed.markAsRead(feedItem);
      runInAction(() => {
        this.notifications = this.notifications?.map((n) =>
          n.uuid === notificationId ? { ...n, dismissed: true } : n
        );
        if (this.unreadCount > 0) {
          this.unreadCount -= 1;
        }
      });
    } catch (e) {
      Logger.error('Failed to mark notification as read', e);
    }
  }

  private static TOKEN_REFRESH_MS = 55 * 60 * 1000;

  private scheduleTokenRefresh(): void {
    this.clearTokenRefresh();
    this.refreshTimer = setTimeout(async () => {
      try {
        const { data } = await this.api.user.getKnockToken({ background: true })();
        if (data.token && data.userId && this.knockClient) {
          this.knockClient.authenticate(data.userId, data.token);
          this.scheduleTokenRefresh();
        }
      } catch (e) {
        Logger.error('Failed to refresh Knock token', e);
      }
    }, KnockInbox.TOKEN_REFRESH_MS);
  }

  private clearTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  @action.bound
  setActiveCategory(category: NotificationCategory): void {
    this.activeCategory = category;
  }

  @action.bound
  setShowUnreadOnly(value: boolean): void {
    this.showUnreadOnly = value;
  }

  @action.bound
  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  @computed
  get filteredNotifications(): NotificationType[] | undefined {
    if (this.notifications === undefined) {
      return undefined;
    }

    let result = this.notifications.filter((n) => getCategoryForNotification(n.target) !== undefined);

    if (this.activeCategory !== NotificationCategory.ALL) {
      result = result.filter((n) => getCategoryForNotification(n.target) === this.activeCategory);
    }

    if (this.showUnreadOnly) {
      result = result.filter((n) => !n.dismissed);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (n) =>
          n.creator.name.toLowerCase().includes(query) ||
          n.instance.name.toLowerCase().includes(query) ||
          (n.instance.subject && n.instance.subject.toLowerCase().includes(query))
      );
    }

    return result;
  }

  @computed
  get unreadFilteredCount(): number {
    return this.filteredNotifications?.filter((n) => !n.dismissed).length ?? 0;
  }

  @computed
  get notDismissedNotificationsCount(): number {
    return this.unreadCount;
  }

  @subscribe(EventType.LoggedOut)
  @action
  logout(): void {
    this.clearTokenRefresh();
    if (this.knockClient) {
      this.knockClient.teardown();
    }
    this.knockClient = null;
    this.knockFeed = null;
    this.notifications = undefined;
    this.unreadCount = 0;
    this.isLoading = false;
    this.hasMore = false;
    this.initialSync = undefined;
    this.isDismissing = false;
    this.activeCategory = NotificationCategory.ALL;
    this.showUnreadOnly = false;
    this.searchQuery = '';
    this.knockEnabled = undefined;
    clearCachedKnockEnabled();
  }
}
